// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { rankRows, svgHistogram, type HistBin } from '../components/charts';
import { openQuakeDrawer, openRegionDrawer } from '../components/detail';
import { gterm, infoI } from '../components/overlays';
import { depthKm, esc, joules, mag as fmtMag, num, relTime, tnt } from '../format';
import {
  depthCategory, energyCompare, energyConcentration, energyJoules, feltVerdict,
  groupBy, magClass, magColor, totalEnergy,
} from '../model';
import { quakes, regionOf, state } from '../state';
import type { Quake } from '../types';
import { periodNoun } from './shared';

export function renderOverview(root: HTMLElement): void {
  const list = quakes();
  if (list.length === 0) { root.innerHTML = `<div class="state-msg"><div class="big">No earthquakes in this window</div>Try a longer time period or a lower magnitude.</div>`; return; }

  const largest = list.reduce((a, b) => (b.mag > a.mag ? b : a));
  const totalE = totalEnergy(list);
  const conc = energyConcentration(list);
  const deepest = list.reduce((a, b) => (b.depth > a.depth ? b : a));
  const mostFelt = list.reduce((a, b) => (b.felt > a.felt ? b : a));
  const tsunamis = list.filter((q) => q.tsunami).length;
  const shallow = list.filter((q) => depthCategory(q.depth) === 'Shallow').length;
  const alerts = list.filter((q) => q.alert === 'orange' || q.alert === 'red').length;

  // magnitude-class distribution
  const classes = ['Micro', 'Minor', 'Light', 'Moderate', 'Strong', 'Major', 'Great'];
  const classCounts = classes.map((c) => list.filter((q) => magClass(q.mag) === c).length);
  const clsMid: Record<string, number> = { Micro: 1, Minor: 3, Light: 4.5, Moderate: 5.5, Strong: 6.5, Major: 7.5, Great: 8.5 };
  const bins: HistBin[] = classes.map((c, i) => ({
    label: c, count: classCounts[i], color: magColor(clsMid[c]),
    tip: `${c}: ${classCounts[i]} event${classCounts[i] === 1 ? '' : 's'}`,
  }));

  const regions = groupBy(list, regionOf).slice(0, 8);
  const maxRegion = Math.max(...regions.map((r) => r.count), 1);
  const regionRows = rankRows(regions.map((g) => ({
    label: g.key, frac: g.count / maxRegion, valueLabel: `${g.count}`,
    color: magColor(g.maxMag), tip: `${g.key}: ${g.count} events, largest M${fmtMag(g.maxMag)}`, id: g.key,
    sub: `max M${fmtMag(g.maxMag)}`,
  })));

  const latest = [...list].sort((a, b) => b.time - a.time).slice(0, 9);

  root.innerHTML = `
    <div class="hero">
      <div>
        <div class="hero-eyebrow">Live · ${esc(state.feed?.title ?? 'USGS Earthquakes')}</div>
        <div class="hero-headline"><span class="big">${num(list.length)}</span> earthquakes in the past ${esc(periodNoun(state.period))}</div>
        <div class="hero-desc">The largest was a magnitude <strong>${fmtMag(largest.mag)}</strong> near ${esc(shortPlace(largest.place))}. ${esc(feltVerdict(largest.mag))}</div>
      </div>
      <div class="hero-verdict">
        <div class="label">Biggest right now ${infoI('magnitude')}</div>
        <div class="mag" style="color:${magColor(largest.mag)}">M ${fmtMag(largest.mag)}</div>
        <div class="muted" style="font-size:0.8rem">${esc(shortPlace(largest.place))} · ${relTime(largest.time)}</div>
        <div style="margin-top:8px"><button class="pill-btn" data-open="${esc(largest.id)}">View detail →</button></div>
      </div>
    </div>

    <div class="grid cols-auto" style="margin-top:1rem">
      ${tile(num(list.length), 'events in window', `${shallow} shallow · ${tsunamis} in tsunami zones`)}
      ${tile('M ' + fmtMag(largest.mag), 'strongest event', magClass(largest.mag))}
      ${tile(joules(totalE), 'total energy released', `≈ ${tnt(totalE)}`)}
      ${tile(Math.round(conc * 100) + '%', 'of energy from one quake', 'the largest event dominates')}
      ${tile(depthKm(deepest.depth), 'deepest event', esc(shortPlace(deepest.place)))}
      ${tile(mostFelt.felt ? num(mostFelt.felt) : '—', 'most-felt reports', mostFelt.felt ? esc(shortPlace(mostFelt.place)) : 'no felt reports yet')}
    </div>

    ${alerts > 0 || tsunamis > 0 ? `<div class="callout" style="margin-top:1rem;border-color:#5a3a1d;background:rgba(249,115,22,0.08)"><strong>${alerts} event${alerts === 1 ? '' : 's'}</strong> carry an orange or red PAGER impact alert${tsunamis ? `, and <strong>${tsunamis}</strong> occurred where a tsunami could form` : ''}. Open the map or explorer to see them.</div>` : ''}

    <div class="row" style="margin-top:1rem;align-items:stretch">
      <div class="panel" style="flex:1 1 340px">
        <div class="panel-title">By magnitude class ${infoI('magnitude')}</div>
        <div class="panel-sub">How the ${num(list.length)} events split by size. Small quakes vastly outnumber large ones ${gterm('gutenberg_richter', '— the Gutenberg–Richter law')}.</div>
        <div class="chart-wrap">${svgHistogram(bins, { height: 220 })}</div>
      </div>
      <div class="panel" style="flex:1 1 340px">
        <div class="panel-title">Most active regions</div>
        <div class="panel-sub">Grouped by tectonic plate. Click a region for its biggest events.</div>
        ${regionRows}
      </div>
    </div>

    <div class="callout" style="margin-top:1rem">
      <strong>Why one number dominates.</strong> Because magnitude is logarithmic, the single largest
      earthquake in this window released about <strong>${Math.round(conc * 100)}%</strong> of all the
      seismic energy — ${esc(energyCompare(largest.mag))}. Thousands of small tremors barely register
      against it. ${gterm('energy', 'More on seismic energy')}.
    </div>

    <div class="panel" style="margin-top:1rem">
      <div class="panel-title">Latest events</div>
      <div class="panel-sub">The nine most recent. Click any for full detail.</div>
      <div class="rank-list">${latest.map(latestRow).join('')}</div>
    </div>`;

  root.querySelector('[data-open]')?.addEventListener('click', () => openQuakeDrawer(largest));
  root.querySelectorAll('.rank-row[data-id]').forEach((r) => r.addEventListener('click', () => {
    const g = regions.find((x) => x.key === r.getAttribute('data-id'));
    if (g) openRegionDrawer(g.key, g.quakes);
  }));
  root.querySelectorAll('[data-event]').forEach((r) => r.addEventListener('click', () => {
    const q = list.find((x) => x.id === r.getAttribute('data-event'));
    if (q) openQuakeDrawer(q);
  }));
}

function tile(value: string, label: string, note: string): string {
  return `<div class="stat-tile"><div class="stat-value sm">${value}</div><div class="stat-label">${label}</div><div class="stat-note">${note}</div></div>`;
}

function latestRow(q: Quake): string {
  return `<div class="rank-row clickable" data-event="${esc(q.id)}" tabindex="0" role="button" data-tip="${esc(q.place)} · ${depthKm(q.depth)} deep · ${joules(energyJoules(q.mag))}">
    <span class="mag-pill" style="background:${magColor(q.mag)}">${fmtMag(q.mag)}</span>
    <div class="rank-label" style="grid-column:span 1">${esc(q.place)}</div>
    <div class="rank-value">${relTime(q.time)}</div>
  </div>`;
}

function shortPlace(place: string): string {
  const idx = place.indexOf(' of ');
  return idx >= 0 ? place.slice(idx + 4) : place;
}
